import {
  Flex,
  Box,
  Button,
  Menu,
  MenuItem,
  MenuList,
  MenuToggle,
  Icon,
} from '@tonic-one/react';
import { FilterAddIcon } from '@tonic-one/react-icons';
import { useMemo, useState } from 'react';
import {
  FeedbackFilterStatus,
  FeedbackFilterCategory,
  FeedbackResultCategory,
  ServiceTypeFilter,
} from '../../type';
import { useTranslation } from 'react-i18next';
import FilterTag from '../components/FilterTag';
import { RequestSourceFilter } from '../hooks/useFilters';

interface FilterItem {
  type:
    | 'status'
    | 'category'
    | 'resultCategory'
    | 'securityActions'
    | 'serviceType'
    | 'requestSource';
  value: string;
  fieldLabel: string;
  valueLabel: string;
}

interface AddFilterMenuProps {
  status?: FeedbackFilterStatus;
  setStatus?: (status: FeedbackFilterStatus) => void;
  category?: FeedbackFilterCategory;
  setCategory?: (category: FeedbackFilterCategory) => void;
  resultCategory?: FeedbackResultCategory;
  setResultCategory?: (resultCategory: FeedbackResultCategory) => void;
  securityActions?: string;
  setSecurityActions?: (securityActions: string) => void;
  serviceType?: ServiceTypeFilter;
  setServiceType?: (serviceType: ServiceTypeFilter) => void;
  requestSource?: RequestSourceFilter;
  setRequestSource?: (requestSource: RequestSourceFilter) => void;
  hideServiceType?: boolean;
  resetSelectionsAndPagination?: () => void;
}

const FILTER_TYPES = [
  'status',
  'category',
  'resultCategory',
  'securityActions',
  'serviceType',
  'requestSource',
] as const;

const FIELD_LABEL_MAP: Record<string, string> = {
  status: 'operations.euq.detail.table.header.approvalStatus',
  category: 'operations.euq.filter.reportedType',
  resultCategory: 'operations.euq.table.analysis',
  securityActions: 'operations.euq.table.securityActions',
  serviceType: 'operations.euq.filter.serviceType',
  requestSource: 'administration.addIn.autoCollect.collectSource.column',
};

const AddFilterMenu = ({
  status,
  setStatus,
  category,
  setCategory,
  resultCategory,
  setResultCategory,
  securityActions,
  setSecurityActions,
  serviceType,
  setServiceType,
  requestSource,
  setRequestSource,
  hideServiceType,
  resetSelectionsAndPagination,
}: AddFilterMenuProps) => {
  const { t } = useTranslation(['operations', 'common']);

  const [activeFilters, setActiveFilters] = useState<Set<string>>(new Set());

  const filterTypes = useMemo(
    () =>
      hideServiceType
        ? FILTER_TYPES.filter((type) => type !== 'serviceType')
        : FILTER_TYPES,
    [hideServiceType]
  );

  const statusLabels = useMemo(
    () => ({
      [FeedbackFilterStatus.All]: t('operations.euq.reportAs.type.all'),
      [FeedbackFilterStatus.Created]: t('operations.euq.statusTab.created'),
      [FeedbackFilterStatus.Approved]: t('operations.euq.statusTab.approved'),
      [FeedbackFilterStatus.Rejected]: t('operations.euq.statusTab.rejected'),
      [FeedbackFilterStatus.Bypassed]: t('operations.euq.statusTab.bypassed'),
    }),
    [t]
  );

  const categoryLabels = useMemo(
    () => ({
      [FeedbackFilterCategory.All]: t('operations.euq.reportAs.type.all'),
      [FeedbackFilterCategory.Spam]: t('operations.euq.reportAs.type.spam'),
      [FeedbackFilterCategory.Phishing]: t(
        'operations.euq.reportAs.type.phishing'
      ),
      [FeedbackFilterCategory.NotSpam]: t(
        'operations.euq.reportAs.type.noRisk'
      ),
    }),
    [t]
  );

  const resultLabels = useMemo(
    () => ({
      [FeedbackResultCategory.All]: t('operations.euq.reportAs.type.all'),
      [FeedbackResultCategory.Spam]: t('operations.euq.analysis.completedSpam'),
      [FeedbackResultCategory.Phishing]: t(
        'operations.euq.analysis.completedPhishing'
      ),
      [FeedbackResultCategory.Flagged]: t(
        'operations.euq.analysis.completedFlagged'
      ),
      [FeedbackResultCategory.Normal]: t(
        'operations.euq.analysis.completedNotARisk'
      ),
      [FeedbackResultCategory.InProgress]: t(
        'operations.euq.analysis.inProgress'
      ),
      [FeedbackResultCategory.Error]: t('operations.euq.analysis.failed'),
    }),
    [t]
  );

  const securityActionLabels: Record<string, string> = useMemo(
    () => ({
      all: t('operations.euq.reportAs.type.all'),
      inProgress: t('operations.euq.action.autoMitigationInProgress'),
      completed: t('operations.euq.action.mitigationCompleted'),
      required: t('operations.euq.action.mitigationRequired'),
    }),
    [t]
  );

  const serviceTypeLabels = useMemo(
    () => ({
      [ServiceTypeFilter.All]: t('operations.euq.reportAs.type.all'),
      [ServiceTypeFilter.Outlook]: t('common:common.service.exchange'),
      [ServiceTypeFilter.Gmail]: t('common:common.service.gmail'),
    }),
    [t]
  );

  const requestSourceLabels = useMemo<Record<RequestSourceFilter, string>>(
    () => ({
      all: t('operations.euq.reportAs.type.all'),
      AutoCollect: t(
        'administration.addIn.autoCollect.collectSource.autoCollect'
      ),
      Plugin: t('administration.addIn.autoCollect.collectSource.plugin'),
      Banner: t('administration.addIn.autoCollect.collectSource.banner'),
      AdminReport: t(
        'administration.addIn.autoCollect.collectSource.adminReport'
      ),
    }),
    [t]
  );

  const filterTags = useMemo(() => {
    const tags: FilterItem[] = [];

    if (status && status !== FeedbackFilterStatus.All) {
      tags.push({
        type: 'status',
        value: status,
        fieldLabel: t(FIELD_LABEL_MAP.status),
        valueLabel: statusLabels[status],
      });
    }

    if (category && category !== FeedbackFilterCategory.All) {
      tags.push({
        type: 'category',
        value: category,
        fieldLabel: t(FIELD_LABEL_MAP.category),
        valueLabel: categoryLabels[category],
      });
    }

    if (resultCategory && resultCategory !== FeedbackResultCategory.All) {
      tags.push({
        type: 'resultCategory',
        value: resultCategory,
        fieldLabel: t(FIELD_LABEL_MAP.resultCategory),
        valueLabel: resultLabels[resultCategory],
      });
    }

    if (securityActions && securityActions !== 'all') {
      tags.push({
        type: 'securityActions',
        value: securityActions,
        fieldLabel: t(FIELD_LABEL_MAP.securityActions),
        valueLabel: securityActionLabels[securityActions] || securityActions,
      });
    }

    if (serviceType && serviceType !== ServiceTypeFilter.All) {
      tags.push({
        type: 'serviceType',
        value: serviceType,
        fieldLabel: t(FIELD_LABEL_MAP.serviceType),
        valueLabel: serviceTypeLabels[serviceType],
      });
    }

    if (requestSource && requestSource !== 'all') {
      tags.push({
        type: 'requestSource',
        value: requestSource,
        fieldLabel: t(FIELD_LABEL_MAP.requestSource),
        valueLabel: requestSourceLabels[requestSource],
      });
    }

    return tags;
  }, [
    status,
    category,
    resultCategory,
    securityActions,
    serviceType,
    requestSource,
    t,
    statusLabels,
    categoryLabels,
    resultLabels,
    securityActionLabels,
    serviceTypeLabels,
    requestSourceLabels,
  ]);

  const availableFilters = useMemo(() => {
    return filterTypes.filter((filterType) => {
      if (filterType === 'status') {
        return !status || status === FeedbackFilterStatus.All;
      } else if (filterType === 'category') {
        return !category || category === FeedbackFilterCategory.All;
      } else if (filterType === 'resultCategory') {
        return !resultCategory || resultCategory === FeedbackResultCategory.All;
      } else if (filterType === 'securityActions') {
        return !securityActions || securityActions === 'all';
      } else if (filterType === 'serviceType') {
        return !serviceType || serviceType === ServiceTypeFilter.All;
      } else if (filterType === 'requestSource') {
        return !requestSource || requestSource === 'all';
      }
      return false;
    });
  }, [
    status,
    category,
    resultCategory,
    securityActions,
    serviceType,
    requestSource,
    filterTypes,
  ]);

  const removeFilterTag = (tag: FilterItem) => {
    if (tag.type === 'status' && setStatus) {
      setStatus(FeedbackFilterStatus.All);
      resetSelectionsAndPagination?.();
    } else if (tag.type === 'category' && setCategory) {
      setCategory(FeedbackFilterCategory.All);
      resetSelectionsAndPagination?.();
    } else if (tag.type === 'resultCategory' && setResultCategory) {
      setResultCategory(FeedbackResultCategory.All);
      resetSelectionsAndPagination?.();
    } else if (tag.type === 'securityActions' && setSecurityActions) {
      setSecurityActions('all');
      resetSelectionsAndPagination?.();
    } else if (tag.type === 'serviceType' && setServiceType) {
      setServiceType(ServiceTypeFilter.All);
      resetSelectionsAndPagination?.();
    } else if (tag.type === 'requestSource' && setRequestSource) {
      setRequestSource('all');
      resetSelectionsAndPagination?.();
    }
    setActiveFilters((prev) => {
      const newSet = new Set(prev);
      newSet.delete(tag.type);
      return newSet;
    });
  };

  const addFilter = (filterType: (typeof FILTER_TYPES)[number]) => {
    setActiveFilters((prev) => new Set(prev).add(filterType));
  };

  return (
    <Flex alignItems="start" gap="2x">
      <Menu>
        <MenuToggle>
          <Button variant="secondary" gap="2x">
            <Icon as={FilterAddIcon} />
            {t('common.action.label.addFilters')}
          </Button>
        </MenuToggle>
        <MenuList width="max-content" minWidth="100%">
          {availableFilters.map((filterType) => (
            <MenuItem key={filterType} onClick={() => addFilter(filterType)}>
              {t(FIELD_LABEL_MAP[filterType])}
            </MenuItem>
          ))}
        </MenuList>
      </Menu>
      {(activeFilters.size > 0 || filterTags.length > 0) && (
        <Flex alignItems="center" gap="2x" flexWrap="wrap" height="100%">
          {activeFilters.size > 0 &&
            Array.from(activeFilters).map((filterType) => {
              const existingTag = filterTags.find((t) => t.type === filterType);
              if (existingTag) return null;

              return (
                <FilterDropdown
                  key={filterType}
                  filterType={filterType}
                  setStatus={setStatus}
                  setCategory={setCategory}
                  setResultCategory={setResultCategory}
                  setSecurityActions={setSecurityActions}
                  setServiceType={setServiceType}
                  setRequestSource={setRequestSource}
                  onRemove={() => {
                    setActiveFilters((prev) => {
                      const newSet = new Set(prev);
                      newSet.delete(filterType);
                      return newSet;
                    });
                  }}
                  onSelect={() => {
                    setActiveFilters((prev) => {
                      const newSet = new Set(prev);
                      newSet.delete(filterType);
                      return newSet;
                    });
                  }}
                  resetSelectionsAndPagination={resetSelectionsAndPagination}
                />
              );
            })}

          {filterTags.map((tag, index) => (
            <FilterDropdown
              key={`${tag.type}-${tag.value}-${index}`}
              filterType={tag.type}
              currentValue={tag.value}
              setStatus={setStatus}
              setCategory={setCategory}
              setResultCategory={setResultCategory}
              setSecurityActions={setSecurityActions}
              setServiceType={setServiceType}
              setRequestSource={setRequestSource}
              onRemove={() => removeFilterTag(tag)}
              onSelect={() => {
                // No need to remove from activeFilters since we're editing an existing filter
              }}
              resetSelectionsAndPagination={resetSelectionsAndPagination}
            />
          ))}
        </Flex>
      )}
    </Flex>
  );
};

const FilterDropdown = ({
  filterType,
  currentValue,
  setStatus,
  setCategory,
  setResultCategory,
  setSecurityActions,
  setServiceType,
  setRequestSource,
  onRemove,
  onSelect,
  resetSelectionsAndPagination,
}: {
  filterType: string;
  currentValue?: string;
  setStatus?: (status: FeedbackFilterStatus) => void;
  setCategory?: (category: FeedbackFilterCategory) => void;
  setResultCategory?: (resultCategory: FeedbackResultCategory) => void;
  setSecurityActions?: (securityActions: string) => void;
  setServiceType?: (serviceType: ServiceTypeFilter) => void;
  setRequestSource?: (requestSource: RequestSourceFilter) => void;
  onRemove: () => void;
  onSelect: () => void;
  resetSelectionsAndPagination?: () => void;
}) => {
  const { t } = useTranslation(['operations', 'common']);

  const getOptions = () => {
    if (filterType === 'status') {
      return [
        {
          value: FeedbackFilterStatus.Created,
          label: t('operations.euq.statusTab.created'),
        },
        {
          value: FeedbackFilterStatus.Approved,
          label: t('operations.euq.statusTab.approved'),
        },
        {
          value: FeedbackFilterStatus.Rejected,
          label: t('operations.euq.statusTab.rejected'),
        },
        {
          value: FeedbackFilterStatus.Bypassed,
          label: t('operations.euq.statusTab.bypassed'),
        },
      ];
    } else if (filterType === 'category') {
      return [
        {
          value: FeedbackFilterCategory.Spam,
          label: t('operations.euq.reportAs.type.spam'),
        },
        {
          value: FeedbackFilterCategory.Phishing,
          label: t('operations.euq.reportAs.type.phishing'),
        },
        {
          value: FeedbackFilterCategory.NotSpam,
          label: t('operations.euq.reportAs.type.noRisk'),
        },
      ];
    } else if (filterType === 'resultCategory') {
      return [
        {
          value: FeedbackResultCategory.InProgress,
          label: t('operations.euq.analysis.inProgress'),
        },
        {
          value: FeedbackResultCategory.Spam,
          label: t('operations.euq.analysis.completedSpam'),
        },
        {
          value: FeedbackResultCategory.Phishing,
          label: t('operations.euq.analysis.completedPhishing'),
        },
        {
          value: FeedbackResultCategory.Normal,
          label: t('operations.euq.analysis.completedNotARisk'),
        },
        {
          value: FeedbackResultCategory.Flagged,
          label: t('operations.euq.analysis.completedFlagged'),
        },
        {
          value: FeedbackResultCategory.Error,
          label: t('operations.euq.analysis.failed'),
        },
      ];
    } else if (filterType === 'securityActions') {
      return [
        {
          value: 'inProgress',
          label: t('operations.euq.action.autoMitigationInProgress'),
        },
        {
          value: 'completed',
          label: t('operations.euq.action.mitigationCompleted'),
        },
        {
          value: 'required',
          label: t('operations.euq.action.mitigationRequired'),
        },
      ];
    } else if (filterType === 'serviceType') {
      return [
        {
          value: ServiceTypeFilter.Outlook,
          label: t('operations.euq.filter.serviceType.outlook'),
        },
        {
          value: ServiceTypeFilter.Gmail,
          label: t('operations.euq.filter.serviceType.gmail'),
        },
      ];
    } else if (filterType === 'requestSource') {
      return [
        {
          value: 'AutoCollect',
          label: t(
            'administration.addIn.autoCollect.collectSource.autoCollect'
          ),
        },
        {
          value: 'Plugin',
          label: t('administration.addIn.autoCollect.collectSource.plugin'),
        },
        {
          value: 'Banner',
          label: t('administration.addIn.autoCollect.collectSource.banner'),
        },
        {
          value: 'CentralOperation',
          label: t(
            'administration.addIn.autoCollect.collectSource.adminReport'
          ),
        },
      ];
    }
    return [];
  };

  const getValueLabel = () => {
    if (!currentValue) return t('common.selectAnOption');
    const options = getOptions();
    const option = options.find((opt) => opt.value === currentValue);
    return option ? option.label : '';
  };

  const handleSelect = (value: string) => {
    if (filterType === 'status' && setStatus) {
      setStatus(value as FeedbackFilterStatus);
    } else if (filterType === 'category' && setCategory) {
      setCategory(value as FeedbackFilterCategory);
    } else if (filterType === 'resultCategory' && setResultCategory) {
      setResultCategory(value as FeedbackResultCategory);
    } else if (filterType === 'securityActions' && setSecurityActions) {
      setSecurityActions(value);
    } else if (filterType === 'serviceType' && setServiceType) {
      setServiceType(value as ServiceTypeFilter);
    } else if (filterType === 'requestSource' && setRequestSource) {
      setRequestSource(value as RequestSourceFilter);
    }
    resetSelectionsAndPagination?.();
    onSelect();
  };

  const fieldLabel = FIELD_LABEL_MAP[filterType]
    ? t(FIELD_LABEL_MAP[filterType])
    : '';

  return (
    <Menu>
      <MenuToggle>
        <Box>
          <FilterTag
            field={fieldLabel}
            value={getValueLabel()}
            onRemove={onRemove}
            isClickable={true}
            maxWidth="none"
          />
        </Box>
      </MenuToggle>
      <MenuList width="max-content" minWidth="100%">
        {getOptions().map((option) => (
          <MenuItem
            key={option.value}
            onClick={() => handleSelect(option.value)}
          >
            {option.label}
          </MenuItem>
        ))}
      </MenuList>
    </Menu>
  );
};

export default AddFilterMenu;
